/**
 * ChronicleCurrentStatus — DEV-1352. The "Current Status" section pinned at the
 * top of a chronicle's About tab (Topic Tracker now-state). Shows the latest
 * dated update narrative + photos, with prev/next nav over filed updates so a
 * reader can page back through history. Authors get New / Edit affordances and
 * the ChronicleStatusWriter modal.
 *
 * Mirrors the Mini-Project StatusSection interaction model (training-ground
 * parity) but stands alone — chronicle status has no schedule/KPI coupling.
 *
 * Empty state: for authors, a friendly "post the first update" prompt. For
 * non-authors with no updates, the section renders nothing (keeps Project
 * Chronicles uncluttered until there's something to say).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { sanitizeRichText } from '../../utils/sanitizeHtml';
import StatusPhotoStrip, { type StatusPhotoItem } from '../StatusPhotoStrip/StatusPhotoStrip';
import { useApiSecurityData } from '../../api/useApiSecurityData';
import ChronicleStatusWriter from './ChronicleStatusWriter';
import {
  listChronicleStatusUpdates,
  getLatestChronicleStatusUpdate,
  getChronicleStatusUpdate,
  type ChronicleStatusUpdate,
  type ChronicleStatusUpdateSummary,
} from '../../api/chronicleStatusUpdates';

interface Props {
  chronicleGuid: string;
  canEdit: boolean;
}

function formatLongDate(iso: string): string {
  try {
    const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}

const ChronicleCurrentStatus: React.FC<Props> = ({ chronicleGuid, canEdit }) => {
  const { t } = useAppTranslation('pages');

  const [summaries, setSummaries] = useState<ChronicleStatusUpdateSummary[]>([]);
  const [current, setCurrent] = useState<ChronicleStatusUpdate | null>(null);
  const [loading, setLoading] = useState(true);
  const [writerOpen, setWriterOpen] = useState(false);
  const [writerMode, setWriterMode] = useState<'create' | 'edit'>('create');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listChronicleStatusUpdates(chronicleGuid);
      setSummaries(list);
      if (list.length > 0) {
        const latest = await getLatestChronicleStatusUpdate(chronicleGuid);
        setCurrent(latest);
      } else {
        setCurrent(null);
      }
    } catch {
      setSummaries([]);
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [chronicleGuid]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const filedDates = useMemo(() => summaries.map((s) => s.reportingDate), [summaries]);

  const navState = useMemo(() => {
    if (!current || summaries.length === 0) {
      return { canPrev: false, canNext: false, prevGuid: null as string | null, nextGuid: null as string | null };
    }
    // summaries are newest-first; [idx+1] = older, [idx-1] = newer.
    const idx = summaries.findIndex((s) => s.statusUpdateGuid === current.statusUpdateGuid);
    if (idx < 0) return { canPrev: false, canNext: false, prevGuid: null, nextGuid: null };
    return {
      canPrev: !!summaries[idx + 1],
      canNext: !!summaries[idx - 1],
      prevGuid: summaries[idx + 1]?.statusUpdateGuid ?? null,
      nextGuid: summaries[idx - 1]?.statusUpdateGuid ?? null,
    };
  }, [current, summaries]);

  const goToGuid = useCallback(
    async (guid: string | null) => {
      if (!guid) return;
      try {
        const full = await getChronicleStatusUpdate(chronicleGuid, guid);
        setCurrent(full);
      } catch {
        /* keep current on failure */
      }
    },
    [chronicleGuid],
  );

  const { securityUsers } = useApiSecurityData();
  const { posterName, posterInitials } = useMemo(() => {
    if (!current) return { posterName: undefined as string | undefined, posterInitials: undefined as string | undefined };
    const guid = current.postedByUserGuid.toUpperCase();
    const rows = (securityUsers || []) as any[];
    const user = rows.find((u) => String(u.user_guid).toUpperCase() === guid);
    const name: string | undefined = user?.display_name || user?.name || user?.screen_name || undefined;
    return { posterName: name, posterInitials: name ? getInitials(name) : '?' };
  }, [current, securityUsers]);

  const photos = useMemo<StatusPhotoItem[]>(
    () =>
      (current?.photos ?? []).map((p) => ({
        storageKey: p.storageKey,
        caption: p.caption ?? null,
        position: p.position,
      })),
    [current],
  );

  // Hide entirely for non-authors when there's nothing to show.
  if (!loading && !current && !canEdit) return null;

  const sectionStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 12,
    padding: 18,
  };
  const navBtnStyle = (enabled: boolean): React.CSSProperties => ({
    inlineSize: 28,
    blockSize: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: '1px solid var(--border-default)',
    background: 'transparent',
    color: enabled ? 'var(--text-secondary)' : 'var(--text-muted)',
    cursor: enabled ? 'pointer' : 'default',
    opacity: enabled ? 1 : 0.5,
  });
  const actionBtnStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
  };

  return (
    <section style={sectionStyle} aria-label={t('chronicleStatus.sectionAria', { defaultValue: 'Current status' })}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBlockEnd: current ? 12 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <span aria-hidden="true" style={{ inlineSize: 8, blockSize: 8, borderRadius: '50%', background: 'var(--color-success)', display: 'inline-block' }} />
            {t('chronicleStatus.title', { defaultValue: 'Current Status' })}
          </h3>
          {current && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                style={navBtnStyle(navState.canPrev)}
                disabled={!navState.canPrev}
                onClick={() => goToGuid(navState.prevGuid)}
                aria-label={t('chronicleStatus.nav.prev', { defaultValue: 'Older update' })}
              >
                ‹
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', minInlineSize: 96, textAlign: 'center' }}>
                {formatLongDate(current.reportingDate)}
              </span>
              <button
                type="button"
                style={navBtnStyle(navState.canNext)}
                disabled={!navState.canNext}
                onClick={() => goToGuid(navState.nextGuid)}
                aria-label={t('chronicleStatus.nav.next', { defaultValue: 'Newer update' })}
              >
                ›
              </button>
            </div>
          )}
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              style={actionBtnStyle}
              onClick={() => { setWriterMode('create'); setWriterOpen(true); }}
            >
              <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>+</span>
              {t('chronicleStatus.newButton', { defaultValue: 'New update' })}
            </button>
            {current && (
              <button
                type="button"
                style={actionBtnStyle}
                onClick={() => { setWriterMode('edit'); setWriterOpen(true); }}
              >
                {t('chronicleStatus.editButton', { defaultValue: 'Edit' })}
              </button>
            )}
          </div>
        )}
      </header>

      {loading ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>
          {t('chronicleStatus.loading', { defaultValue: 'Loading…' })}
        </div>
      ) : current ? (
        <>
          <div
            style={{ color: 'var(--text-primary)', lineHeight: 1.65, fontSize: 15 }}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(current.narrativeHtml) }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBlockStart: 10, color: 'var(--text-tertiary)', fontSize: 12 }}>
            <span
              aria-hidden="true"
              style={{
                inlineSize: 22, blockSize: 22, borderRadius: '50%', background: 'var(--bg-elevated)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)',
              }}
            >
              {posterInitials || '?'}
            </span>
            <span>
              {posterName || current.postedByUserGuid.slice(0, 8) + '…'}
            </span>
            <span aria-hidden="true">·</span>
            <span>{t('chronicleStatus.postedOn', { defaultValue: 'Updated {{date}}', date: formatLongDate(current.reportingDate) })}</span>
          </div>
          {photos.length > 0 && (
            <div style={{ marginBlockStart: 12 }}>
              <StatusPhotoStrip mode="read" photos={photos} />
            </div>
          )}
        </>
      ) : (
        // Author-only empty prompt.
        <div style={{ color: 'var(--text-tertiary)', fontSize: 14, marginBlockStart: 8, lineHeight: 1.6 }}>
          {t('chronicleStatus.empty.authorTip', {
            defaultValue:
              "Post a status update to tell readers where things stand right now. The latest update always shows at the top.",
          })}
        </div>
      )}

      <ChronicleStatusWriter
        open={writerOpen}
        onClose={() => setWriterOpen(false)}
        chronicleGuid={chronicleGuid}
        mode={writerMode}
        editingStatus={writerMode === 'edit' ? current : null}
        filedDates={filedDates}
        onSaved={() => { void loadAll(); }}
      />
    </section>
  );
};

export default ChronicleCurrentStatus;
